import { cx } from '@linaria/core';
import { useRef, useEffect } from 'react';
import { CSSTransition } from 'react-transition-group';
import { useComponentFrameworkState } from '@provider/component-framework';
import { resets } from '@ui/css';
import { typesetKatex } from '@utils/common';
import { useTransitionState, IsomorphicPortal } from '@utils/react';
import { TRANSITION_DURATION_MS } from './constants.js';
import { useA11y } from './hook.a11y.js';
import { useActiveState } from './hook.active-state.js';
import { usePosition } from './hook.position.js';
import { tooltip, label, shortcut, arrow } from './tooltip.styles.js';
import { jsx, Fragment, jsxs } from 'react/jsx-runtime';

function Tooltip(props) {
  const {
    pointAt = "container"
  } = props;
  const internalContainerRef = useRef(null);
  const containerRef = props.containerRef ?? internalContainerRef;
  const internalTogglerRef = useRef(null);
  const togglerRef = props.togglerRef ?? internalTogglerRef;
  const labelRef = useRef(null);
  const active = useActiveState(props, containerRef, togglerRef);
  const transitionState = useTransitionState(active, TRANSITION_DURATION_MS);
  const {
    floating,
    arrowRef
  } = usePosition(props, pointAt === "container" ? containerRef : togglerRef, active || transitionState === "exit-active");
  useEffect(() => {
    if (!active) {
      return;
    }
    typesetKatex(labelRef);
  }, [active]);

  // keep a consistent aria-describedby applied to the toggler button
  const tooltipId = useA11y(props, containerRef, togglerRef);
  const targetSelector = useComponentFrameworkState(state => state.overlayContainerSelector);
  if (!props.body && !props.shortcut || props.disabled) {
    return /*#__PURE__*/jsx(Fragment, {
      children: props.children(containerRef, togglerRef)
    });
  }
  const target = targetSelector && document.querySelector(targetSelector) || props.mountIn || defaultContainer;
  return /*#__PURE__*/jsxs(Fragment, {
    children: [props.children(containerRef, togglerRef), /*#__PURE__*/jsx(IsomorphicPortal, {
      target: target,
      children: /*#__PURE__*/jsx(CSSTransition, {
        in: active,
        timeout: TRANSITION_DURATION_MS,
        classNames: tooltip,
        nodeRef: floating.refs.floating,
        mountOnEnter: props.ariaType === undefined,
        unmountOnExit: props.ariaType === undefined,
        children: /*#__PURE__*/jsxs("div", {
          ref: floating.refs.setFloating,
          id: tooltipId,
          className: cx(resets.rootCls, tooltip, props.className, active && "visible"),
          "data-placement": floating.placement,
          role: "tooltip",
          style: floating.floatingStyles,
          children: [/*#__PURE__*/jsxs("span", {
            ref: labelRef,
            className: label,
            children: [props.body, props.shortcut !== undefined && /*#__PURE__*/jsx("span", {
              className: shortcut,
              children: props.shortcut.replace(/^cmd(\+|-)/i, "⌘").replace(/\+\+/, "+").replace(/-\+/, "+").replace(/\+-/, "-")
            })]
          }), /*#__PURE__*/jsx("span", {
            ref: arrowRef,
            className: arrow,
            style: {
              left: floating.middlewareData.arrow?.x,
              top: floating.middlewareData.arrow?.y
            }
          })]
        })
      }, String(props.body))
    })]
  });
}
let defaultContainer = undefined;

/**
 * allows configuration of a default tooltip container that will be used for
 * mounting tooltips, unless another mountIn prop is explicitly provided to the
 * <Tooltip> element
 */
function configureTooltipContainer(node) {
  defaultContainer = node;
}

export { Tooltip, configureTooltipContainer };
//# sourceMappingURL=tooltip.js.map
